import * as R from 'ramda'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useFuse } from '$func/hooks'
import { Invitee } from '$logic/invite/types'
import { getNonSignedUpContacts } from '$logic/contacts/service'
import { getInvitedContacts } from '$logic/invite/service'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { invitationInfoView } from '$logic/invite/storage'
import { getUserDisplayName } from '$logic/users'
import { compareString } from '$func/sort'
import { isMobilePhone } from '$func/phone'

export function useFetchInvitableUsers() {
  const { value: nonSignedUpContacts = [] } = useAsyncFunction(
    getNonSignedUpContacts,
    [],
  )
  const { loading, value: invitedContacts } = useAsyncFunction(
    getInvitedContacts,
    [],
  )

  return {
    items: useMemo(
      () =>
        nonSignedUpContacts
          .filter(({ phone }) => isMobilePhone(phone))
          .map(contact => ({
            ...R.omit(['id', 'updatedAt'], contact),
            invited: loading ? false : invitedContacts.includes(contact.phone),
          }))
          .sort((a, b) =>
            compareString(getUserDisplayName(a), getUserDisplayName(b)),
          ),
      [loading, nonSignedUpContacts],
    ) as Invitee[],
    loading,
  }
}

export function useSearchedInviteUsers({
  query,
  searchedItems,
}: {
  query: string
  searchedItems: Invitee[]
}) {
  const results = useFuse({
    items: searchedItems,
    keys: ['firstname', 'lastname', 'phone'],
    query,
  })
  return {
    searchedItems: results,
  }
}

export function useCanInvite() {
  return useSelector(state => invitationInfoView(state)?.canInvite)
}

export function useClientInviter() {
  return useSelector(state => invitationInfoView(state)?.inviter)
}
